import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Store } from 'redux';
import { LicenseManager } from '@ag-grid-enterprise/core';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import store, { AppState, persistor } from './store';
import serviceContainer, { ServiceContainer } from 'src/ServiceContainer';
import { ToastContainer } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import NavigationShell from 'src/pages/NavigationShell/NavigationShell.container';
import PerspectiveSelection from 'src/pages/PerspectiveSelection/PerspectiveSelection.container';
import { HashRouter as Router, Route, Routes } from 'react-router-dom';
import { updateConfDefns, updateConfigFingerPrint } from 'src/services/configuration/AppConfig.slice';
import { enableAllPlugins } from 'immer';
import { fetchFingerprintFactory } from 'src/services/configuration';
import { PersistGate } from 'redux-persist/integration/react';

import { Overlay } from './common-ui';
import { ServerSentEventMediator } from './services/ServerSentEvents/ServerSentEventMediator';

import '../node_modules/@fortawesome/fontawesome-pro/css/all.css';
import '../node_modules/npm-font-open-sans/open-sans.scss';

import './_global.scss';
import './global.css';
import { AuthContext } from 'react-oauth2-code-pkce';
import { Platform } from './platform';

enableAllPlugins();

LicenseManager.setLicenseKey(
  'Using_this_AG_Grid_Enterprise_key_( AG-044159 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( S5 Stratos )_is_granted_a_( Single Application )_Developer_License_for_the_application_( S5Stratos )_only_for_( 5 )_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_( S5Stratos )_need_to_be_licensed___( S5Stratos )_has_been_granted_a_Deployment_License_Add-on_for_( 5 )_Production_Environments___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 25 June 2024 )____[v2]_MTcxOTI3MDAwMDAwMA==143c7acbabc441693cfa6492d8b5ccd4',
);

const { printService, configService } = serviceContainer;

const ServerSentEventMediatorInstance = new ServerSentEventMediator();

// The root application node
function App(): React.ReactElement {
  const [configError, setConfigError] = React.useState<Error | null>(null);
  const [configLoaded, setConfigLoaded] = React.useState<boolean>(false);

  const { token } = React.useContext(AuthContext);

  React.useEffect(() => {
    window.__debug.bootState = store.getState();
  });

  React.useEffect(() => {
    ServerSentEventMediatorInstance.receiveToken(store.dispatch, token);
  }, [token]);

  React.useEffect(() => {
    fetchFingerprintFactory()
      .then((fingerprint) => {
        store.dispatch(updateConfigFingerPrint(fingerprint));
      })
      .catch((e) => {
        console.error('Config has no fingerprint.'); // eslint-disable-line no-console
        setConfigError(e instanceof Error ? e : new Error('Unknown error fetching config fingerprint'));
        // We should have auth to send this log here
        serviceContainer.loggingService.error(
          'Error fetching config fingerprint: ' +
            (e instanceof Error ? e.message : 'Unknown error fetching config fingerprint'),
        );
      });
  }, []);

  // The initialization logic for the service container (config factory specifically) is a bit wonky and async
  // Lots of components below here assume config is fully loaded so we also have to block

  React.useEffect(() => {
    const errors = configService.errored$.subscribe((e) => {
      setConfigError(e);
    });

    const configs = configService.configuration$.subscribe(async (uiConfs) => {
      store.dispatch(updateConfDefns(uiConfs));
      setConfigLoaded(true);
    });

    return () => {
      errors.unsubscribe();
      configs.unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    // setup print service
    printService.hydrate(store);
    printService.setPrintFromUrl();
    window.onpopstate = printService.setPrintFromUrl;
  });

  if (configError) {
    return (
      <Overlay
        visible={true}
        fitParent={true}
        type={'error'}
        text={'Unable to load configuration. Please refresh the page.'}
      />
    );
  }

  if (!configLoaded) {
    return <Overlay visible={true} fitParent={true} type={'loading'} />;
  }

  return (
    <React.Fragment>
      <PersistGate loading={null} persistor={persistor}>
        <Router>
          <Routes>
            <Route path="/" element={<PerspectiveSelection />} />
            {/* Because of nested routes, only care about matching perspective  */}
            {/* @ts-ignore TODO fix router types */}
            <Route path={':perspective/*'} element={<NavigationShell />} />
          </Routes>
        </Router>
      </PersistGate>
      <ToastContainer autoClose={5000} />
    </React.Fragment>
  );
}

ReactDOM.render(
  <Platform>
    <App />
  </Platform>,
  document.getElementById('root'),
);

declare global {
  interface Window {
    __debug: {
      store: Store<AppState>;
      serviceContainer: ServiceContainer;
      bootState?: AppState;
    };
  }
}

window.__debug = {
  store,
  serviceContainer,
};
